import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { HousePhoto } from './HouseFront';

/**
 * "O outro lado" — back of the house shown as an EDGE-TO-EDGE cinematic
 * band. Mirror of HouseFront but with copy BELOW the image for rhythm
 * variation. Source back.jpg is 1600x548 (≈ 2.92:1) — narrower than
 * the front, so the band is taller; keeping the native aspect-ratio
 * means no cropping or stretching regardless of viewport.
 */
export function HouseBack() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  // Opposite-direction drift from HouseFront so the two photo moments
  // don't feel mechanically identical.
  const imgX = useTransform(scrollYProgress, [0, 1], ['2.5%', '-2.5%']);
  const imgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.06, 1, 1.04]);

  const textY = useTransform(scrollYProgress, [0.35, 0.65], [30, 0]);
  const textO = useTransform(scrollYProgress, [0.3, 0.6], [0, 1]);

  return (
    <section
      ref={ref}
      className="relative border-t border-line bg-bg py-24 sm:py-32"
      aria-label="Fundos da casa — piscina e deck"
    >
      {/* Full-bleed photo, native aspect ratio. */}
      <motion.div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: '1600 / 548' }}
        initial={{ opacity: 0.6 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-15%' }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div
          className="absolute inset-0 will-change-transform"
          style={{ x: imgX, scale: imgScale, transform: 'translateZ(0)' }}
        >
          <HousePhoto
            src="/genese/back.jpg"
            alt="Piscina, deck e área social de Gênese"
            placeholderTone="cool"
            // Wave 299c · responsive variants generated at build time.
            widths={[800, 1200, 1600]}
            sizes="100vw"
          />
        </motion.div>
      </motion.div>

      <motion.div
        className="mx-auto mt-12 max-w-7xl px-6 sm:mt-16 sm:px-12 lg:px-20"
        style={{ opacity: textO, y: textY }}
      >
        <div className="max-w-3xl">
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
            O outro lado
          </p>
          <h2 className="mt-5 font-display text-[clamp(2rem,5vw,3rem)] font-light leading-[1.05] tracking-tighter text-ink [text-wrap:balance]">
            Onde a casa abre mão de parede.
          </h2>
          <p className="mt-5 max-w-[52ch] text-sm leading-relaxed text-ink-muted sm:text-base">
            Dezessete metros de vidro correm de ponta a ponta. A cozinha,
            a sala e o jantar compartilham a mesma luz e o mesmo deck —
            sem divisória, sem degrau, sem transição. A piscina fica a
            três passos do fogão.
          </p>
        </div>
      </motion.div>
    </section>
  );
}
